import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Trash2, Eye, Package, CreditCard, User, Plus, X } from 'lucide-react';

const ColumnView = ({
  column, draggedOverColumn, hoveredCard, setHoveredCard,
  handleDragStart, handleDragEnd, handleDragOver,
  handleDragEnter, handleDragLeave, handleDrop, openModal, fullWidth
}) => (
  <div className={`flex flex-col flex-shrink-0 h-full ${fullWidth ? 'w-full' : 'w-[288px]'}`}>
    <div className="flex items-center gap-2 mb-3 px-1 flex-shrink-0">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${column.dot}`} />
      <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{column.title}</span>
      <span className="bg-[#fff8ef] text-slate-600 text-xs font-semibold px-2 py-0.5 rounded-full">
        {column.filtered.length}
      </span>
    </div>

    <div
      className={`flex-1 overflow-y-auto ${column.bg} border ${column.border} border-t-4 ${column.accentBorder} rounded-xl p-3 space-y-3 transition-all duration-200 ${
        draggedOverColumn === column.id ? `ring-2 ${column.dragRing} ring-opacity-60 scale-[1.01]` : ''
      }`}
      onDragOver={handleDragOver}
      onDragEnter={() => handleDragEnter(column.id)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, column.id)}
    >
      {column.filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
          <Package className="w-8 h-8 opacity-30" />
          <span>No orders</span>
        </div>
      )}

      {column.filtered.map((task) => (
        <div
          key={task.id}
          draggable
          onDragStart={(e) => handleDragStart(e, task, column.id)}
          onDragEnd={handleDragEnd}
          onMouseEnter={() => setHoveredCard(task.id)}
          onMouseLeave={() => setHoveredCard(null)}
          onClick={() => openModal(task, column.id)}
          className={`bg-white rounded-xl p-3.5 cursor-grab active:cursor-grabbing border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
            hoveredCard === task.id ? 'border-teal-300 shadow-md' : 'border-slate-200 shadow-sm'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-xs text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md truncate max-w-40">
              {task.id}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); openModal(task, column.id); }}
              className="p-1 rounded-md hover:bg-slate-100 transition-colors flex-shrink-0 ml-1"
            >
              <Eye className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>

          <div className="flex items-center gap-2.5 mb-3">
            <div className={`${task.avatarBg} w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold`}>
              {task.initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-700 truncate leading-tight">{task.customer}</p>
              <p className="text-xs text-slate-400 truncate leading-tight">{task.email}</p>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
            <span>{task.date}</span>
            <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-medium">{task.payMode}</span>
          </div>

          <div className="flex items-center justify-between pt-2.5 border-t border-slate-100">
            <span className="font-mono text-sm font-bold text-slate-800">{task.total}</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              task.payStatus === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {task.payStatus}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const OrdersKanban = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayMode, setSelectedPayMode] = useState([]);
  const [selectedPayStatus, setSelectedPayStatus] = useState([]);
  const [showPayModeFilter, setShowPayModeFilter] = useState(false);
  const [showPayStatusFilter, setShowPayStatusFilter] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeColumn, setActiveColumn] = useState('placed');

  useEffect(() => {
    // Prevent page-level scroll — all scrolling happens inside the board
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-dropdown')) {
        setShowPayModeFilter(false);
        setShowPayStatusFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [columns, setColumns] = useState([
    {
      id: 'placed', title: 'Placed',
      accentBorder: 'border-t-teal-500', bg: 'bg-teal-50', border: 'border-teal-200',
      dragRing: 'ring-teal-400', dot: 'bg-teal-500',
      tasks: [
        { id: 'ORD-2QEGGZTLFJ', customer: 'Superrr Admin', email: 'sudhakar144@gmail.com', date: 'Mar 9, 2026', payMode: 'COD', payStatus: 'Paid', total: '₹425.00', initials: 'SA', avatarBg: 'bg-indigo-500' },
        { id: 'ORD-MHHR4C5OJB', customer: 'Su Rekha', email: 'surekha.inbox1116@gmail.com', date: 'Mar 9, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹222.92', initials: 'SR', avatarBg: 'bg-pink-500' },
        { id: 'ORD-KFXFAODKCP', customer: 'B.v Sudhakar', email: 'sudhakarbv144@gmail.com', date: 'Mar 5, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹220.91', initials: 'BS', avatarBg: 'bg-orange-500' },
        { id: 'ORD-KWGW5220HE', customer: 'B.v Sudhakar', email: 'sudhakarbv144@gmail.com', date: 'Mar 5, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹220.91', initials: 'BS', avatarBg: 'bg-orange-500' },
        { id: 'ORD-GUDOOTMJVJ', customer: 'Hello He', email: 'hello123@gmail.com', date: 'Mar 2, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹24.99', initials: 'HH', avatarBg: 'bg-cyan-500' },
      ]
    },
    {
      id: 'processing', title: 'Processing',
      accentBorder: 'border-t-blue-500', bg: 'bg-blue-50', border: 'border-blue-200',
      dragRing: 'ring-blue-400', dot: 'bg-blue-500',
      tasks: [
        { id: 'ORD-P1XZAQBMNK', customer: 'Priya Sharma', email: 'priya.sharma@email.com', date: 'Mar 8, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹1,240.00', initials: 'PS', avatarBg: 'bg-violet-500' },
        { id: 'ORD-P2RTYLMNOP', customer: 'Ravi Kumar', email: 'ravi.kumar@gmail.com', date: 'Mar 8, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹580.50', initials: 'RK', avatarBg: 'bg-teal-500' },
        { id: 'ORD-P3MNBVCXZA', customer: 'Anita Desai', email: 'anita.d@outlook.com', date: 'Mar 7, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹3,450.00', initials: 'AD', avatarBg: 'bg-rose-500' },
      ]
    },
    {
      id: 'shipped', title: 'Shipped',
      accentBorder: 'border-t-violet-500', bg: 'bg-violet-50', border: 'border-violet-200',
      dragRing: 'ring-violet-400', dot: 'bg-violet-500',
      tasks: [
        { id: 'ORD-S1QWERTYLP', customer: 'Mohan Das', email: 'mohan.das@gmail.com', date: 'Mar 6, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹780.00', initials: 'MD', avatarBg: 'bg-sky-500' },
        { id: 'ORD-S2ASDFGHJK', customer: 'Lakshmi R', email: 'lakshmi.r@yahoo.com', date: 'Mar 5, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹2,100.75', initials: 'LR', avatarBg: 'bg-fuchsia-500' },
        { id: 'ORD-S3ZXCVBNML', customer: 'Kiran Patel', email: 'kiran.patel@gmail.com', date: 'Mar 4, 2026', payMode: 'COD', payStatus: 'Paid', total: '₹345.20', initials: 'KP', avatarBg: 'bg-green-500' },
        { id: 'ORD-S4POIUYTRE', customer: 'Deepa N', email: 'deepa.n@gmail.com', date: 'Mar 3, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹990.00', initials: 'DN', avatarBg: 'bg-amber-500' },
      ]
    },
    {
      id: 'delivered', title: 'Delivered',
      accentBorder: 'border-t-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200',
      dragRing: 'ring-emerald-400', dot: 'bg-emerald-500',
      tasks: [
        { id: 'ORD-E9CN3UEDKP', customer: 'Sahithi Kandula', email: 'kandulasahithi3006@gmail.com', date: 'Mar 9, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹10,340.36', initials: 'SK', avatarBg: 'bg-emerald-600' },
        { id: 'ORD-D2LMNOPQRS', customer: 'Arjun Reddy', email: 'arjun.reddy@gmail.com', date: 'Mar 7, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹4,500.00', initials: 'AR', avatarBg: 'bg-indigo-500' },
        { id: 'ORD-D3TUVWXYZ1', customer: 'Sunita Mehta', email: 'sunita.mehta@email.com', date: 'Mar 6, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹1,875.50', initials: 'SM', avatarBg: 'bg-cyan-600' },
        { id: 'ORD-D4ABCDEFGH', customer: 'Vijay Krishnan', email: 'vijay.k@gmail.com', date: 'Mar 4, 2026', payMode: 'COD', payStatus: 'Paid', total: '₹650.00', initials: 'VK', avatarBg: 'bg-red-500' },
        { id: 'ORD-D5IJKLMNOP', customer: 'Meena S', email: 'meena.s@outlook.com', date: 'Mar 2, 2026', payMode: 'COD', payStatus: 'Paid', total: '₹320.75', initials: 'MS', avatarBg: 'bg-purple-500' },
      ]
    },
    {
      id: 'cancelled', title: 'Cancelled',
      accentBorder: 'border-t-red-500', bg: 'bg-red-50', border: 'border-red-200',
      dragRing: 'ring-red-400', dot: 'bg-red-500',
      tasks: [
        { id: 'ORD-ALMPDGSNIJ', customer: 'abcd ab', email: 'abcd@gmail.com', date: 'Mar 6, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹360.07', initials: 'AA', avatarBg: 'bg-slate-500' },
        { id: 'ORD-DSCUYLHGPH', customer: 'Hello He', email: 'hello123@gmail.com', date: 'Mar 2, 2026', payMode: 'Card', payStatus: 'Paid', total: '₹239.91', initials: 'HH', avatarBg: 'bg-cyan-500' },
        { id: 'ORD-C3QRSTUVWX', customer: 'Rahul Nair', email: 'rahul.nair@gmail.com', date: 'Feb 28, 2026', payMode: 'COD', payStatus: 'Pending', total: '₹180.00', initials: 'RN', avatarBg: 'bg-orange-500' },
      ]
    }
  ]);

  const handleDragStart = (e, task, columnId) => {
    setDraggedCard({ task, sourceColumnId: columnId });
    e.currentTarget.style.opacity = '0.4';
  };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = '1'; setDraggedCard(null); setDraggedOverColumn(null); };
  const handleDragOver = (e) => e.preventDefault();
  const handleDragEnter = (columnId) => setDraggedOverColumn(columnId);
  const handleDragLeave = () => setDraggedOverColumn(null);
  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedCard) return;
    const { task, sourceColumnId } = draggedCard;
    if (sourceColumnId === targetColumnId) { setDraggedOverColumn(null); return; }
    setColumns(prev => prev.map(col => {
      if (col.id === sourceColumnId) return { ...col, tasks: col.tasks.filter(t => t.id !== task.id) };
      if (col.id === targetColumnId) return { ...col, tasks: [...col.tasks, task] };
      return col;
    }));
    setDraggedOverColumn(null);
  };

  const toggleFilter = (val, list, setList) =>
    setList(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  const clearAllFilters = () => { setSearchQuery(''); setSelectedPayMode([]); setSelectedPayStatus([]); };
  const hasActiveFilters = searchQuery || selectedPayMode.length > 0 || selectedPayStatus.length > 0;

  const filterTasks = (tasks) => tasks.filter(task => {
    if (searchQuery && !task.id.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !task.customer.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedPayMode.length > 0 && !selectedPayMode.includes(task.payMode)) return false;
    if (selectedPayStatus.length > 0 && !selectedPayStatus.includes(task.payStatus)) return false;
    return true;
  });

  const openModal = (task, columnId) => {
    setSelectedCard({ ...task, columnId });
    setFormData({ ...task, status: columnId });
    setShowModal(true);
  };

  const handleSave = () => {
    if (selectedCard) {
      setColumns(prev => prev.map(col => {
        if (col.id === selectedCard.columnId) return { ...col, tasks: col.tasks.filter(t => t.id !== selectedCard.id) };
        if (col.id === formData.status) return { ...col, tasks: [...col.tasks, { ...selectedCard, ...formData }] };
        return col;
      }));
    } else {
      const newTask = {
        id: `ORD-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
        customer: formData.customer || 'New Customer',
        email: formData.email || '',
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        payMode: formData.payMode || 'COD',
        payStatus: formData.payStatus || 'Pending',
        total: formData.total || '₹0.00',
        initials: (formData.customer || 'NC').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase(),
        avatarBg: 'bg-teal-500',
      };
      setColumns(prev => prev.map(col =>
        col.id === (formData.status || 'placed') ? { ...col, tasks: [...col.tasks, newTask] } : col
      ));
    }
    setShowModal(false);
  };

  const handleDelete = () => {
    setColumns(prev => prev.map(col => ({ ...col, tasks: col.tasks.filter(t => t.id !== selectedCard.id) })));
    setShowModal(false);
  };

  const visibleColumns = columns.map(col => ({ ...col, filtered: filterTasks(col.tasks) }));

  return (
    <div className="h-screen w-full flex flex-col bg-slate-100 overflow-hidden" style={{ maxWidth: '100%' }}>
      {/* Toolbar */}
    

      {/* Mobile Column Tabs */}
      <div className="lg:hidden bg-white border-b border-slate-200 overflow-x-auto flex-shrink-0">
        <div className="flex min-w-max px-3 py-2 gap-1">
          {columns.map(col => {
            const count = filterTasks(col.tasks).length;
            return (
              <button
                key={col.id}
                onClick={() => setActiveColumn(col.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeColumn === col.id ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {col.title}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeColumn === col.id ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 min-h-0 relative">
        {/* Desktop: all columns - scroll ONLY inside this box */}
        <div className="hidden lg:flex absolute inset-0 gap-4 overflow-x-auto overflow-y-hidden p-4 pb-3 scroll-smooth ">
          {visibleColumns.map(column => (
            <ColumnView
              key={column.id}
              column={column}
              draggedOverColumn={draggedOverColumn}
              hoveredCard={hoveredCard}
              setHoveredCard={setHoveredCard}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              openModal={openModal}
            />
          ))}
        </div>

        {/* Mobile: single active column */}
        <div className="lg:hidden absolute inset-0 overflow-y-auto p-4">
          {visibleColumns.filter(col => col.id === activeColumn).map(column => (
            <ColumnView
              key={column.id}
              column={column}
              draggedOverColumn={draggedOverColumn}
              hoveredCard={hoveredCard}
              setHoveredCard={setHoveredCard}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              openModal={openModal}
              fullWidth
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <h2 className="font-semibold text-slate-800 text-base">
                {selectedCard ? 'Edit Order' : 'Add Order'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {selectedCard && (
                <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                  <span className="font-mono text-xs text-slate-500">{selectedCard.id}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Customer', key: 'customer', type: 'input', placeholder: 'Customer name' },
                  { label: 'Email', key: 'email', type: 'input', placeholder: 'Email address' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">{field.label}</label>
                    <input
                      value={formData[field.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                ))}

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Status</label>
                  <select value={formData.status || 'placed'} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white">
                    <option value="placed">Placed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Payment Mode</label>
                  <select value={formData.payMode || 'COD'} onChange={e => setFormData(p => ({ ...p, payMode: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white">
                    <option value="COD">COD</option>
                    <option value="Card">Card</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Pay Status</label>
                  <select value={formData.payStatus || 'Pending'} onChange={e => setFormData(p => ({ ...p, payStatus: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white">
                    <option value="Paid">Paid</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Total</label>
                  <input
                    value={formData.total || ''}
                    onChange={e => setFormData(p => ({ ...p, total: e.target.value }))}
                    placeholder="₹0.00"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <button onClick={handleSave}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors">
                  {selectedCard ? 'Update Order' : 'Create Order'}
                </button>
                <button onClick={() => setShowModal(false)}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors">
                  Cancel
                </button>
                {selectedCard && (
                  <button onClick={handleDelete} className="ml-auto p-2.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersKanban;